import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarController,
  BarElement,
} from 'chart.js';
import { originTickPlugin, prepareChartData } from './utils.js';
import { DEFAULT_SMOOTHING_INTERVAL } from './constants.js';
import {
  tooltipConfig,
  registerTooltipPositioner,
} from './chartConfig/tooltip.js';
import { createScalesConfig } from './chartConfig/scales.js';
import { createDatasets } from './chartConfig/datasets.js';
import {
  createDefaultActiveStateSetter,
  createMouseHandlers,
} from './chartConfig/callbacks.js';
import { removeSkeleton } from './skeletonLoader.js';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarController,
  BarElement,
);

// Register defaults with loaded fonts
Chart.defaults.font.family = "'Roboto Mono', 'Graphik', sans-serif";

// Register plugins
Chart.register(originTickPlugin);

// Register custom tooltip positioner
registerTooltipPositioner();

const createChart = async (container, data, options = {}) => {
  // Create canvas element dynamically inside the container
  const canvas = document.createElement('canvas');

  // Apply essential canvas styles to ensure proper behavior
  canvas.style.maxWidth = '100%';
  canvas.style.height = 'auto';
  canvas.style.display = 'block';
  canvas.style.opacity = '0';
  canvas.style.transition = 'opacity 300ms ease-in';

  // Remove skeleton loader before clearing container and wait for fade-out
  await removeSkeleton(container);

  // Clear any existing content in the container
  container.innerHTML = '';

  // Append the canvas to the container
  container.appendChild(canvas);

  // Trigger fade-in immediately since skeleton is already gone
  setTimeout(() => {
    canvas.style.opacity = '1';
  }, 10);

  const { dayInterval = DEFAULT_SMOOTHING_INTERVAL } = options;
  const processedData = prepareChartData(data, dayInterval);
  const {
    fullDateLabels,
    labels,
    dataMean,
    maxYScale,
    tooltipDataMap,
    projectedFlags,
  } = processedData;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 2,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    layout: {
      padding: {
        right: 5,
      },
    },
    scales: createScalesConfig(maxYScale),
    plugins: {
      legend: {
        display: false,
      },
      tooltip: tooltipConfig,
    },
  };

  const datasets = createDatasets(processedData);

  let chart;
  try {
    chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets,
        fullDateLabels,
        tooltipDataMap,
        projectedFlags,
      },
      options: chartOptions,
    });
  } catch (error) {
    console.error('Failed to create Chart.js instance:', error);
    // Return a minimal fallback object to prevent further crashes
    return {
      canvas,
      setActiveElements: () => {},
      update: () => {},
    };
  }

  const lastIndex = dataMean.length - 1;

  // Set up chart interactions
  const setDefaultActiveState = createDefaultActiveStateSetter(
    chart,
    lastIndex,
  );

  // Set initial default state
  setDefaultActiveState();

  // Set up mouse event handlers
  const mouseHandlers = createMouseHandlers(setDefaultActiveState);
  chart.canvas.addEventListener('mouseenter', mouseHandlers.mouseenter);
  chart.canvas.addEventListener('mouseleave', mouseHandlers.mouseleave);

  return chart;
};

export default createChart;
